'use client'

import { BagCheck } from 'solar-icons'
import { toast } from 'sonner'
import { useCart, useModals } from '@/lib/hooks'

import {
  Badge,
  Barcode,
  Button,
  Input,
  Label,
  Separator,
  Thumbnail,
} from '@/components/ui'

interface AddCartItemSerialProps
  extends React.ComponentPropsWithoutRef<'form'> {
  product: Product
}

export const AddCartItemSerial = ({ product }: AddCartItemSerialProps) => {
  const { addSerialNumber } = useCart() as any
  const { closeModal } = useModals()

  const cartId = product?.cart_id
  const productId = product?.id

  const handleSubmit = async (data: FormData) => {
    try {
      await addSerialNumber({
        cartId,
        productId,
        serialNumber: data.get('serialNumber'),
      })

      closeModal('ADD_CART_ITEM_SERIAL')
    } catch (error) {
      toast.error('Failed to add serial number.')
    }
  }

  const fallbackTitle = `${product?.product_name} ${product?.varient_options}`

  return (
    <form
      className="flex w-full flex-wrap gap-4 lg:gap-8"
      action={handleSubmit}
    >
      <Thumbnail size="lg" src={product?.image_url} />

      <div className="flex w-full max-w-[396px] flex-col flex-wrap gap-4 pr-4">
        <h2 className="text-balance text-xl font-medium">
          {product?.variant_label ?? fallbackTitle}
        </h2>

        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="w-[36px] px-0" size="md">
                SKU
              </Badge>
              <span className="font-mono text-sm font-medium text-gray-950">
                {product?.sku}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="min-w-5 px-1" size="md">
                <Barcode className="h-3" />
              </Badge>
              <span className="font-mono text-sm font-medium text-gray-950">
                {product?.barcode}
              </span>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-5">
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center gap-2">
              <Label className="shrink-0 !text-sm font-medium text-gray-700">
                Serial Number
              </Label>
              <Separator className="mt-1" />
            </div>

            <div className="flex items-center gap-2">
              <Input
                className="w-full appearance-none text-center"
                inputSize="lg"
                name="serialNumber"
                type="text"
              />
            </div>
          </div>
        </div>

        <div className="flex w-full gap-2 pt-3">
          <Button
            variant="brand"
            className="w-full gap-3 shadow-card"
            size="lg"
            type="submit"
          >
            <BagCheck className="size-5" strokeWidth={1.5} />
            <span>Save Serial Number</span>
          </Button>
        </div>
      </div>
    </form>
  )
}
